// src/pages/AdminView.js
import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Home as HomeIcon,
  AssignmentInd as AssignIcon,
  Logout as LogoutIcon,
  Event as EventIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { getRoleFromToken } from '../utils/auth';

function AdminView() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // determine current user role to toggle sidebar items
  let currentRole = null;
  try {
    currentRole = getRoleFromToken();
  } catch (err) {
    currentRole = null;
  }
  const drawerWidth = 260;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', icon: <HomeIcon />, label: 'Dashboard' },
    { path: '/assign', icon: <AssignIcon />, label: 'Assign Questions' },
    // only superadmin can see Sessions management
    ...(currentRole === 'admin' || currentRole === 'superadmin' ? [{ path: '/subjects', icon: <SettingsIcon />, label: 'Subjects' }] : []),
    ...(currentRole === 'superadmin' ? [{ path: '/sessions', icon: <EventIcon />, label: 'Sessions' }] : []),
  ];

  const drawerContent = (
    <>
      {/* Header */}
      <Box sx={{ p: { xs: 2, md: 3 }, textAlign: 'center' }}>
        <Typography 
          variant="h2" 
          sx={{ 
            fontFamily: '"Poppins", "Inter", sans-serif',
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            color: '#fff',
            mb: 1.5,
            letterSpacing: '-0.5px',
          }}
        >
          CBT
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1rem', md: '1.25rem' } }}>
          Admin Panel
        </Typography>
        <Typography variant="caption" sx={{ color: '#bdc3c7', fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
          CBT Management System
        </Typography>
      </Box>

      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', mx: 2, mb: 2 }} />

      {/* Menu Items */}
      <List sx={{ px: 2 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
              <ListItemButton 
                component={Link} 
                to={item.path}
                onClick={() => isMobile && setMobileOpen(false)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  bgcolor: isActive ? 'rgba(102, 126, 234, 0.2)' : 'transparent',
                  color: isActive ? '#fff' : '#bdc3c7',
                  '&:hover': {
                    bgcolor: isActive ? 'rgba(102, 126, 234, 0.3)' : 'rgba(255,255,255,0.05)',
                    color: '#fff',
                  },
                  transition: 'all 0.3s ease',
                  borderLeft: isActive ? '4px solid #667eea' : '4px solid transparent',
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '0.95rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Logout Button */}
      <Box sx={{ position: 'absolute', bottom: 0, width: '100%', p: 2 }}>
        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', mb: 2 }} />
        <ListItemButton 
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            py: 1.5,
            color: '#e74c3c',
            '&:hover': {
              bgcolor: 'rgba(231, 76, 60, 0.1)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout" 
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile Menu Button */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300,
            bgcolor: '#667eea',
            color: 'white',
            '&:hover': {
              bgcolor: '#5568d3',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: 'linear-gradient(180deg, #2c3e50 0%, #34495e 100%)',
            color: '#fff',
            borderRight: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: '#f5f7fa',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default AdminView;
